import type { ReactNode } from 'react';

import { cn } from '../lib/utils';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './collapsible';
import { Separator } from './separator';

type Props = {
    trigger: ReactNode;
    children?: ReactNode;
    open?: boolean;
    defaultOpen?: boolean;
    hideSeparator?: boolean;
    onOpenChange?: (open: boolean) => void;
    contentClassName?: string;
    /**
     * When true (default), the trigger is wrapped in `CollapsibleTrigger asChild` so
     * clicking/activating it toggles the disclosure and gains the matching ARIA semantics.
     * Set to false when the trigger has its own role (e.g. a checkbox) and a parent state
     * already drives `open` — wrapping in that case causes ARIA conflicts and double-toggles.
     */
    triggerAsToggle?: boolean;
};

export function TreeNode({
    trigger,
    children,
    open,
    defaultOpen,
    hideSeparator = false,
    onOpenChange,
    contentClassName,
    triggerAsToggle = true,
}: Props) {
    const triggerElement = triggerAsToggle ? (
        <CollapsibleTrigger asChild>{trigger}</CollapsibleTrigger>
    ) : (
        trigger
    );
    return (
        <Collapsible
            className="flex flex-col gap-4"
            open={open}
            defaultOpen={defaultOpen}
            onOpenChange={onOpenChange}
        >
            <div className="flex items-center gap-2">{triggerElement}</div>
            {children && (
                <CollapsibleContent
                    className={cn('flex items-stretch gap-4 pb-2 pl-6', contentClassName)}
                >
                    {!hideSeparator && (
                        <Separator
                            orientation="vertical"
                            className="data-[orientation=vertical]:h-auto"
                        />
                    )}
                    {children}
                </CollapsibleContent>
            )}
        </Collapsible>
    );
}
